import React from 'react';
import { Box, Typography, Chip, Paper } from '@mui/material';
import type { LedgerEntry } from '../../types';

export interface BadNumbersWinnersSectionProps {
  ledgerEntries: LedgerEntry[];
}

/**
 * Displays bad number payouts grouped by player.
 * Shows when Winners toggle is ON and there are bad_numbers ledger entries.
 */
export const BadNumbersWinnersSection: React.FC<BadNumbersWinnersSectionProps> = ({ ledgerEntries }) => {
  const badNumberEntries = (ledgerEntries || []).filter(entry => entry.entryType === 'bad_numbers');
  if (badNumberEntries.length === 0) return null;

  // Group entries by user/offline player
  const groupedByPlayer = badNumberEntries.reduce((acc, entry) => {
    const key = entry.userId || entry.offlinePlayerId || 'unknown';
    if (!acc[key]) {
      acc[key] = {
        id: key,
        name: entry.user?.displayName || entry.offlinePlayer?.displayName || 'Unknown',
        entries: [],
        total: 0,
      };
    }
    acc[key].entries.push(entry);
    acc[key].total += entry.amount;
    return acc;
  }, {} as Record<string, { id: string; name: string; entries: typeof badNumberEntries; total: number }>);

  return (
    <Box sx={{ mt: 2 }} data-testid="bad-numbers-winners-section">
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'inherit', opacity: 0.8, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          label="Bad #"
          size="small"
          sx={{
            fontSize: '0.65rem',
            height: 20,
            bgcolor: 'error.light',
            color: 'error.dark',
          }}
        />
        Bad Number Winners
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {Object.values(groupedByPlayer).map((player) => (
          <Paper
            key={player.id}
            sx={{
              p: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              bgcolor: 'rgba(255,255,255,0.9)',
            }}
            data-testid="bad-numbers-winner-entry"
          >
            <Box>
              <Typography variant="body2" fontWeight="bold" data-testid="bad-numbers-winner-name">
                {player.name}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
                {player.entries.map((entry) => (
                  <Typography key={entry.id} variant="caption" color="text.secondary" data-testid="bad-numbers-winner-detail">
                    {entry.description} - ${entry.amount.toFixed(2)}
                  </Typography>
                ))}
              </Box>
            </Box>
            <Typography variant="body2" fontWeight="bold" color="success.main" data-testid="bad-numbers-winner-total">
              ${player.total.toFixed(2)}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};
